import * as React from "react";
import { cn } from "../../lib/utils";
import { SearchField } from "./Filters";
import { SavedViewChips, type SavedViewChipItem } from "./SavedViewChips";
import { MoreFiltersPanel } from "./MoreFiltersPanel";
import { ActiveFilterChips, type ActiveFilterChip } from "./ActiveFilterChips";

/**
 * ListToolbar — the control band over a list page, in three rows (§3).
 *
 * <pre>
 *   row 1   [ search …………………………… ] [ lens ]                       [ sort ▾ ]
 *   row 2   [ view ] [ view ] [ view ]  [ primary controls ]  [ More filters (N) ] [ actions ]
 *   row 3   [ Status: Engaged ✕ ] [ Source: Referral ✕ ]  Clear all      ← only when any
 * </pre>
 *
 * <p>Every slot is optional and a row renders only when something in it does,
 * so a page that has no saved views and no hidden filters gets exactly the
 * one-line bar it had. The toolbar owns LAYOUT and nothing else: filter state,
 * persistence, what a view means and which filters are "more" are the host's,
 * which is what lets four list pages share one band without inheriting each
 * other's nouns.</p>
 *
 * <p>Slot vocabulary, so the four pages place the same thing in the same place:
 * `lens` is the mine-vs-everyone `ScopeToggle` (§9.2); `primary` is the one or
 * two page-specific controls that earn a place at rest (a team select for a
 * manager, say); `moreFilters` is everything that does not; `actions` is
 * table-level furniture such as a column chooser or an export button.</p>
 */
export interface ListToolbarProps {
  search?: {
    value: string;
    onChange: (value: string) => void;
    /** Enumerates the fields actually searched (§3.1). */
    placeholder: string;
    label?: string;
  };
  lens?: React.ReactNode;
  views?: {
    items: SavedViewChipItem[];
    activeKey: string;
    onSelect: (key: string) => void;
    label?: string;
  };
  primary?: React.ReactNode;
  moreFilters?: {
    content: React.ReactNode;
    activeCount: number;
    label?: string;
  };
  activeChips?: ActiveFilterChip[];
  onClearAllChips?: () => void;
  sort?: React.ReactNode;
  actions?: React.ReactNode;
  className?: string;
  "data-testid"?: string;
}

const ROW_CLASS = "flex flex-wrap items-center gap-space-3";

export function ListToolbar({
  search,
  lens,
  views,
  primary,
  moreFilters,
  activeChips,
  onClearAllChips,
  sort,
  actions,
  className,
  "data-testid": testId,
}: ListToolbarProps) {
  const rowOne = search || lens || sort;
  const rowTwo = views || primary || moreFilters || actions;
  const rowThree = (activeChips?.length ?? 0) > 0;

  return (
    <div
      className={cn("mb-space-3 flex flex-col gap-space-3", className)}
      data-testid={testId}
    >
      {rowOne && (
        <div className={ROW_CLASS} data-testid={testId ? `${testId}-row-search` : undefined}>
          {search && (
            <SearchField
              value={search.value}
              onChange={search.onChange}
              placeholder={search.placeholder}
              label={search.label ?? "Search"}
              className="min-w-[16rem] grow basis-[20rem]"
            />
          )}
          {lens}
          {sort && <div className="ml-auto">{sort}</div>}
        </div>
      )}

      {rowTwo && (
        <div className={ROW_CLASS} data-testid={testId ? `${testId}-row-views` : undefined}>
          {views && (
            <SavedViewChips
              items={views.items}
              activeKey={views.activeKey}
              onSelect={views.onSelect}
              label={views.label}
              data-testid={testId ? `${testId}-views` : undefined}
            />
          )}
          {primary}
          {(moreFilters || actions) && (
            <div className="ml-auto flex flex-wrap items-center gap-space-2">
              {moreFilters && (
                <MoreFiltersPanel
                  activeCount={moreFilters.activeCount}
                  label={moreFilters.label}
                  data-testid={testId ? `${testId}-more-filters` : undefined}
                >
                  {moreFilters.content}
                </MoreFiltersPanel>
              )}
              {actions}
            </div>
          )}
        </div>
      )}

      {rowThree && (
        <ActiveFilterChips
          chips={activeChips ?? []}
          onClearAll={onClearAllChips}
          data-testid={testId ? `${testId}-active` : undefined}
        />
      )}
    </div>
  );
}
